import { MessageSquare, Workflow, FileText, Database, User } from "lucide-react";

/**
 * The Agentic Engine diagram on the About page.
 * A central hexagon (KyroLabs Agentic Engine) with 5 satellite nodes
 * connected by faint dashed paths that pulse. Pure SVG + a lucide icon
 * overlay per node so it stays crisp at any density.
 */
export default function AgenticEngineDiagram({ accent = "#8fd0ff" }) {
    const W = 520;
    const H = 400;
    const cx = W / 2;
    const cy = H / 2;

    // 5 satellite positions around the hexagon (top-left, top-right,
    // right, bottom-center, left) matching the reference image.
    const nodes = [
        { id: "conv", label: "Conversations", Icon: MessageSquare, x: 78, y: 108 },
        { id: "flow", label: "Workflows", Icon: Workflow, x: W - 78, y: 108 },
        { id: "know", label: "Knowledge", Icon: FileText, x: 78, y: 292 },
        { id: "sys", label: "Systems", Icon: Database, x: W - 78, y: 292 },
        { id: "people", label: "People", Icon: User, x: cx, y: H - 34 },
    ];

    // Hexagon vertices centred at (cx, cy) with radius r (flat-top).
    const r = 72;
    const hex = [0, 60, 120, 180, 240, 300]
        .map((deg) => {
            const rad = (Math.PI / 180) * deg;
            return `${cx + r * Math.cos(rad)},${cy + r * Math.sin(rad)}`;
        })
        .join(" ");

    return (
        <div
            style={{
                position: "relative",
                width: "100%",
                maxWidth: W,
                aspectRatio: `${W} / ${H}`,
            }}
        >
            <svg
                viewBox={`0 0 ${W} ${H}`}
                width="100%"
                height="100%"
                fill="none"
                aria-hidden
            >
                <defs>
                    <radialGradient id="hexGlow" cx="50%" cy="50%" r="70%">
                        <stop offset="0%" stopColor={accent} stopOpacity="0.35" />
                        <stop offset="100%" stopColor={accent} stopOpacity="0" />
                    </radialGradient>
                    <linearGradient id="hexFill" x1="0" x2="1" y1="0" y2="1">
                        <stop offset="0%" stopColor="#0d1a2a" />
                        <stop offset="100%" stopColor="#1a2a45" />
                    </linearGradient>
                </defs>

                {/* Connection lines from each node to the hex centre */}
                {nodes.map((n) => (
                    <line
                        key={`ln-${n.id}`}
                        x1={n.x}
                        y1={n.y}
                        x2={cx}
                        y2={cy}
                        stroke={accent}
                        strokeOpacity="0.35"
                        strokeWidth="1"
                        strokeDasharray="3 4"
                    />
                ))}

                {/* Soft radial glow behind the hex */}
                <circle cx={cx} cy={cy} r={r * 1.9} fill="url(#hexGlow)" />

                {/* Central hexagon */}
                <polygon
                    points={hex}
                    fill="url(#hexFill)"
                    stroke={accent}
                    strokeOpacity="0.7"
                    strokeWidth="1.5"
                />
                {/* Inner cube icon (stylised) */}
                <g transform={`translate(${cx - 18} ${cy - 40})`}>
                    <path
                        d="M18 4 L34 12 L34 30 L18 38 L2 30 L2 12 Z"
                        fill={accent}
                        fillOpacity="0.9"
                        stroke={accent}
                        strokeWidth="1"
                    />
                    <path
                        d="M18 4 L18 22 M2 12 L18 22 L34 12"
                        stroke="#0a0d12"
                        strokeWidth="1.4"
                    />
                </g>
                <text
                    x={cx}
                    y={cy + 12}
                    fill="#ffffff"
                    fontSize="12"
                    fontWeight="600"
                    textAnchor="middle"
                    fontFamily="Inter, system-ui, sans-serif"
                >
                    KyroLabs
                </text>
                <text
                    x={cx}
                    y={cy + 28}
                    fill={accent}
                    fillOpacity="0.9"
                    fontSize="10"
                    textAnchor="middle"
                    fontFamily="Inter, system-ui, sans-serif"
                >
                    Agentic Engine
                </text>

                {/* Satellite nodes: circle + label */}
                {nodes.map((n) => (
                    <g key={n.id}>
                        <circle
                            cx={n.x}
                            cy={n.y}
                            r="26"
                            fill="#0d1a2a"
                            stroke={accent}
                            strokeOpacity="0.6"
                            strokeWidth="1"
                        />
                    </g>
                ))}
            </svg>

            {/* Overlay lucide icons + labels on the satellite nodes */}
            {nodes.map((n) => {
                const Icon = n.Icon;
                // Label placement: below for bottom node, else on the outer side.
                const isBottom = n.y > cy + 60;
                const isRight = n.x > cx + 40;
                const isLeft = n.x < cx - 40;
                const labelStyle = {
                    position: "absolute",
                    color: "#e8ecf3",
                    fontSize: 12,
                    letterSpacing: 0.2,
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                };
                if (isBottom) {
                    Object.assign(labelStyle, {
                        left: `${(n.x / W) * 100}%`,
                        top: `${((n.y + 40) / H) * 100}%`,
                        transform: "translateX(-50%)",
                    });
                } else if (isRight) {
                    Object.assign(labelStyle, {
                        left: `${((n.x + 32) / W) * 100}%`,
                        top: `${(n.y / H) * 100}%`,
                        transform: "translateY(-50%)",
                    });
                } else if (isLeft) {
                    Object.assign(labelStyle, {
                        left: `${((n.x - 32) / W) * 100}%`,
                        top: `${(n.y / H) * 100}%`,
                        transform: "translate(-100%, -50%)",
                    });
                }
                return (
                    <div key={`ov-${n.id}`}>
                        <div
                            style={{
                                position: "absolute",
                                left: `${(n.x / W) * 100}%`,
                                top: `${(n.y / H) * 100}%`,
                                transform: "translate(-50%, -50%)",
                                pointerEvents: "none",
                            }}
                        >
                            <Icon size={20} color={accent} strokeWidth={1.6} />
                        </div>
                        <div style={labelStyle}>{n.label}</div>
                    </div>
                );
            })}
        </div>
    );
}
